import { useEffect, useState } from "react";
import { Bot, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const KIND_LABEL = { "ai-summary": "Samenvatting", "extract-pdf": "PDF jaarverslag", "extract-depreciations": "PDF afschrijvingen", "extract-demo-pdf": "Demo-PDF", "extract-liquidity": "Kasstroom uit jaarverslag" };
const fmtTime = (iso) => (iso ? new Date(iso + (iso.endsWith("Z") ? "" : "Z")).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

function Kinds({ kinds, field }) {
  const parts = Object.entries(kinds || {}).filter(([, v]) => v[field] > 0).map(([k, v]) => `${KIND_LABEL[k] || k} ${v[field]}`);
  return <span className="text-[11px] text-muted-foreground">{parts.length ? parts.join(" · ") : "—"}</span>;
}

export default function AiUsageBlock() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { setBusy(true); api.get("/admin/ai-usage").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setBusy(false)); };
  useEffect(load, []);
  if (!data) return null;
  const limit = data.hourly_limit;
  return (
    <section className="bg-card border-2 border-foreground heavy-shadow p-6 mb-8" data-testid="ai-usage-block">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="stat-label">Kosten &amp; misbruik</div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight inline-flex items-center gap-2"><Bot className="w-5 h-5" /> AI-gebruik per gebruiker</h2>
          <p className="text-sm text-muted-foreground mt-1">
            AI-samenvattingen en PDF-extracties (niet de helpchat). Limiet: {limit} aanvragen per uur per gebruiker; beheerders tellen mee maar zijn niet begrensd.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="border-2 border-foreground px-3 py-2 bg-background" data-testid="ai-usage-total-today">
            <div className="font-display uppercase text-[10px] tracking-widest text-muted-foreground">Vandaag</div>
            <div className="font-mono-num font-bold text-xl">{data.totals.today}</div>
          </div>
          <div className="border-2 border-foreground px-3 py-2 bg-background" data-testid="ai-usage-total-week">
            <div className="font-display uppercase text-[10px] tracking-widest text-muted-foreground">7 dagen</div>
            <div className="font-mono-num font-bold text-xl">{data.totals.week}</div>
          </div>
          <button onClick={load} disabled={busy} data-testid="ai-usage-refresh" title="Vernieuwen"
            className="btn-brutal bg-background text-foreground px-2.5 py-2 disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /></button>
        </div>
      </div>
      {data.users.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="ai-usage-empty">Geen AI-aanvragen in de afgelopen 7 dagen.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ai-usage-table">
            <thead>
              <tr className="bg-foreground text-background">
                {["Gebruiker", "Tier", "Vandaag", "7 dagen", "Laatste aanvraag"].map((h, i) => (
                  <th key={h} className={`px-3 py-2 font-display uppercase text-xs tracking-widest ${i >= 2 && i <= 3 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => {
                const hot = u.today >= limit;
                return (
                  <tr key={u.user_id} data-testid={`ai-usage-row-${u.user_id}`} className={`border-b border-foreground/10 ${hot ? "bg-destructive/10" : ""}`}>
                    <td className="px-3 py-2">
                      <div className="font-bold font-mono-num text-xs break-all">{u.email}</div>
                      <div className="text-xs text-muted-foreground">{u.name || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-xs font-bold tracking-widest">{u.tier || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <div className={`font-mono-num font-bold ${hot ? "text-destructive" : ""}`} data-testid={`ai-usage-today-${u.user_id}`}>{u.today}</div>
                      <Kinds kinds={u.kinds} field="today" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-mono-num font-bold">{u.week}</div>
                      <Kinds kinds={u.kinds} field="week" />
                    </td>
                    <td className="px-3 py-2 font-mono-num text-xs">{fmtTime(u.last)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
